<template>
    <BaseCard v-if="dimension && current" as="section" padding="none" class="grid min-w-0 grid-cols-1 md:grid-cols-[12.5rem_minmax(0,1fr)]">
        <nav class="flex flex-col gap-1 border-b border-card-border p-3 md:border-r md:border-b-0" :aria-label="t('teamPage.lensNavigation')">
            <div v-for="item in comparison.dimensions" :key="item.id" class="flex flex-col gap-1">
                <BaseButton
                    theme="tertiary"
                    shape="square"
                    :class="{ 'bg-surface': dimension.id === item.id && !lens }"
                    :aria-expanded="dimension.id === item.id"
                    :aria-controls="`${navigationId}-${item.id}`"
                    :aria-pressed="dimension.id === item.id && !lens"
                    class="flex min-h-10 w-full items-center justify-between gap-2 px-3 py-2 text-left text-ink"
                    @click="selectDimension(item.id)">
                    <span :class="dimension.id === item.id ? 'body-small-bold' : 'body-small'">{{ t(`teamPage.dimensionTitles.${item.id}`) }}</span>
                    <i class="bi text-ink-muted" :class="dimension.id === item.id ? 'bi-chevron-down' : 'bi-chevron-right'" aria-hidden="true" />
                </BaseButton>
                <div v-show="dimension.id === item.id" :id="`${navigationId}-${item.id}`" class="ml-5 flex flex-wrap gap-1 border-l border-card-border pl-3 md:flex-col">
                    <BaseButton
                        v-for="facet in item.lenses"
                        :key="facet.id"
                        theme="tertiary"
                        shape="square"
                        size="sm"
                        :class="{ 'bg-surface': lens?.id === facet.id }"
                        :aria-pressed="dimension.id === item.id && lens?.id === facet.id"
                        class="justify-start whitespace-normal px-3 py-2 text-left text-ink"
                        @click="selection = { kind: 'lens', dimensionId: item.id, lensId: facet.id }">
                        <span :class="lens?.id === facet.id ? 'body-small-semibold' : 'body-small'">{{ lensTitle(item.id, facet) }}</span>
                    </BaseButton>
                </div>
            </div>
        </nav>
        <div class="flex min-w-0 flex-col gap-5 p-4 md:p-6">
            <h2 class="body-serif text-ink">{{ questions[dimension.id] }}</h2>
            <PairScoreScale
                :dimension="dimension.id"
                :viewer="current.viewer"
                :colleague="current.colleague"
                :colleague-name="comparison.colleague_name"
                :viewer-archetype="comparison.viewer_archetype"
                :colleague-archetype="comparison.colleague_archetype"
                :left-label="current.left_label"
                :right-label="current.right_label" />
            <template v-if="lens">
                <template v-if="lens.guidance">
                    <div class="flex flex-col">
                        <h3 class="body-small-semibold text-ink">{{ t("pairDetail.together") }}</h3>
                        <p class="body-small text-ink-muted">{{ t("pairDetail.readingSentence", { reading }) }}</p>
                        <ul class="body-small ml-4 list-disc space-y-1 text-ink-muted">
                            <li v-for="point in lens.guidance.points" :key="point" class="whitespace-pre-line">{{ point }}</li>
                        </ul>
                    </div>
                    <div class="flex flex-col">
                        <h3 class="body-small-semibold text-ink">{{ t("pairDetail.tryTogether") }}</h3>
                        <ul class="body-small ml-4 list-disc space-y-1 text-ink-muted">
                            <li v-for="idea in lens.guidance.solution_ideas" :key="idea" class="whitespace-pre-line">{{ idea }}</li>
                        </ul>
                    </div>
                </template>
                <p v-else class="body-small text-ink-muted">{{ t("pairDetail.noGuidance") }}</p>
            </template>
            <template v-else-if="dimension.overview">
                <div class="flex flex-col">
                    <h3 class="body-small-semibold text-ink">{{ t("pairDetail.together") }}</h3>
                    <p class="body-small whitespace-pre-line text-ink-muted">{{ t("pairDetail.togetherSummary", { reading, summary: dimension.overview.tip }) }}</p>
                </div>
                <div class="flex flex-col">
                    <h3 class="body-small-semibold text-ink">{{ t("pairDetail.tryTogether") }}</h3>
                    <p class="body-small whitespace-pre-line text-ink-muted">{{ dimension.overview.action }}</p>
                </div>
            </template>
            <p v-else class="body-small text-ink-muted">
                {{ t("pairDetail.readingSentence", { reading }) }} {{ dimension.lenses.length ? t("pairDetail.noOverview") : t("pairDetail.noOverviewNoLenses") }}
            </p>
            <p v-if="current.viewer.binned_score === 0 || current.colleague.binned_score === 0" class="body-small text-ink-muted">{{ t("pairDetail.balanced") }}</p>
            <div v-if="!lens && dimension.discussion_prompts.length" class="-mb-3 border-t border-card-border pt-2">
                <BaseAccordion
                    :accordion-ids="['discussion']"
                    class="-mx-2"
                    item-class="overflow-visible bg-transparent"
                    header-class="rounded-lgr"
                    trigger-class="min-h-0 rounded-lgr px-2 py-2 text-ink"
                    content-class="px-2 pb-3">
                    <template #trigger-discussion
                        ><span class="body-small-semibold">{{ t("pairDetail.discussion") }}</span></template
                    >
                    <template #content-discussion>
                        <ul class="body-small mt-2 ml-4 list-disc space-y-2 text-ink-muted">
                            <li v-for="prompt in dimension.discussion_prompts" :key="prompt">{{ prompt }}</li>
                        </ul>
                    </template>
                </BaseAccordion>
            </div>
            <div class="flex flex-col gap-3 border-t border-card-border pt-6">
                <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                    <p class="scaffold-details-small text-ink-muted">{{ t("pairDetail.privateChat") }}</p>
                    <BaseButton theme="neutral" size="sm" :disabled="isStartingChat" @click="discuss">
                        {{
                            isStartingChat
                                ? t("pairDetail.opening")
                                : t("pairDetail.discussSelection", {
                                      topic: lens ? lensTitle(dimension.id, lens).toLowerCase() : t(`teamPage.dimensionTitles.${dimension.id}`).toLowerCase(),
                                  })
                        }}
                    </BaseButton>
                </div>
                <p v-if="chatError" role="alert" class="body-small text-ink">{{ chatError }}</p>
            </div>
        </div>
    </BaseCard>
</template>
<script setup lang="ts">
import type { PairComparison, PairLens } from "~vue/api/generated/pair-perspective";
import BaseAccordion from "~vue/components/BaseAccordion.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import PairScoreScale from "~vue/Connections/PairScoreScale.vue";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ comparison: PairComparison; isStartingChat: boolean; chatError: string | null }>();
const emit = defineEmits<{ discuss: [message: string] }>();
const { t, te } = useI18n();
const navigationId = useId();
const questions = computed<Record<string, string>>(() => ({
    EI: t("pairDetail.questions.EI"),
    SN: t("pairDetail.questions.SN"),
    TF: t("pairDetail.questions.TF"),
    JP: t("pairDetail.questions.JP"),
}));
const differenceLabels = computed(() => ({
    neutral: t("pairDetail.differenceLabels.neutral"),
    medium: t("pairDetail.differenceLabels.medium"),
    strong: t("pairDetail.differenceLabels.strong"),
}));
const similarityLabels = computed(() => ({
    neutral: t("pairDetail.similarityLabels.neutral"),
    similar: t("pairDetail.similarityLabels.similar"),
    different: t("pairDetail.similarityLabels.different"),
}));
type Selection = { kind: "dimension"; dimensionId: string } | { kind: "lens"; dimensionId: string; lensId: string };
const selection = ref<Selection | null>(null);
const dimension = computed(() => props.comparison.dimensions.find((item) => item.id === selection.value?.dimensionId) ?? props.comparison.dimensions[0]);
const lens = computed(() => {
    const selected = selection.value;
    return selected?.kind === "lens" ? dimension.value?.lenses.find((item) => item.id === selected.lensId) : undefined;
});
const current = computed(() => lens.value ?? dimension.value);
const reading = computed(() => {
    if (lens.value) {
        return similarityLabels.value[lens.value.similarity];
    }
    return dimension.value ? differenceLabels.value[dimension.value.difference] : "";
});
function selectDimension(dimensionId: string) {
    selection.value = { kind: "dimension", dimensionId };
}
watch(
    () => props.comparison,
    () => {
        const selected = selection.value;
        if (!dimension.value) {
            selection.value = null;
        } else if (!selected || selected.dimensionId !== dimension.value.id || (selected.kind === "lens" && !lens.value)) {
            selectDimension(dimension.value.id);
        }
    },
    { immediate: true },
);
function lensTitle(dimensionId: string, facet: PairLens) {
    const id = facet.id.startsWith(`${dimensionId}:`) ? facet.id.slice(dimensionId.length + 1) : facet.id;
    return te(`teamPage.lenses.${id}`) ? t(`teamPage.lenses.${id}`) : facet.title;
}
function discuss() {
    if (!dimension.value) {
        return;
    }
    const topic = lens.value ?? dimension.value;
    const action = lens.value ? lens.value.guidance?.solution_ideas[0] : dimension.value.overview?.action || dimension.value.discussion_prompts[0];
    emit(
        "discuss",
        t("connections.pairPerspective.discussDimensionMessage", {
            dimension: topic.title.toLowerCase(),
            name: props.comparison.colleague_name,
            followUp: action || t("connections.pairPerspective.defaultFollowUp"),
        }),
    );
}
</script>
